import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  class: "quest-flow",
  "aria-label": "任务衔接图"
}
const _hoisted_2 = { class: "flow-toolbar" }
const _hoisted_3 = { class: "scope-control" }
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "flow-controls" }
const _hoisted_6 = {
  key: 0,
  class: "flow-warning",
  role: "status"
}
const _hoisted_7 = ["title"]
const _hoisted_8 = { class: "group-kind" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { class: "group-count" }
const _hoisted_11 = {
  key: 0,
  class: "group-empty"
}
const _hoisted_12 = { class: "node-meta" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = ["title"]
const _hoisted_15 = ["title"]
const _hoisted_16 = {
  key: 0,
  class: "node-ports"
}
const _hoisted_17 = {
  key: 0,
  class: "finish-tag"
}
const _hoisted_18 = {
  key: 1,
  class: "flow-empty"
}
const _hoisted_19 = { class: "flow-footer" }
const _hoisted_20 = {
  key: 0,
  class: "selection-actions"
}
const _hoisted_21 = {
  key: 1,
  class: "selection-actions"
}
const _hoisted_22 = { key: 2 }
const _hoisted_23 = {
  key: 3,
  class: "flow-pages"
}
const _hoisted_24 = ["disabled"]
const _hoisted_25 = ["disabled"]

import StudioSelectField from "../StudioSelectField.vue";
import { computed, inject, nextTick, onActivated, onBeforeUnmount, onDeactivated, ref, watch } from "vue";
import { studioEditorActiveKey } from "../studioSessionHistory";
import { VueFlow, Handle, Position, MarkerType, useVueFlow, type Node, type Edge, type Connection, type NodeDragEvent, type NodeMouseEvent, type EdgeMouseEvent } from "@vue-flow/core";
import { Background } from "@vue-flow/background";
import { toast } from "vue-sonner";
import type { QuestProject, QuestSelection } from "./types";
import { buildQuestFlow, connectQuestFlow, disconnectQuestFlow, layoutQuestFlowGroups, fitQuestFlowGroupBounds, questFlowAbsolutePositions, questNodeId, QUEST_FLOW_PAGE_SIZE, type QuestFlowBox, type QuestFlowEdge } from "./questFlow";
import "@vue-flow/core/dist/style.css";
import "@vue-flow/core/dist/theme-default.css";


export default /*@__PURE__*/_defineComponent({
  __name: 'QuestFlowGraph',
  props: {
    project: {},
    selection: {}
  },
  emits: ["select", "edit"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const flowId = `quest-flow-${crypto.randomUUID()}`;
const editorActive = inject(studioEditorActiveKey, () => true);
const { fitView, zoomIn, zoomOut, panBy } = useVueFlow(flowId);
const panning = ref(false);
let cancelPan: (() => void) | undefined;

// Group backgrounds also carry Vue Flow's no-pan class. Capture their left drags,
// while leaving group headings, task cards and connection handles to native node interactions.
function startCanvasPan(event: PointerEvent) {
  if (!editorActive()) return;
  const target = event.target as Element | null;
  const groupBackground = target?.closest(".flow-group") && !target.closest(".group-heading");
  if (event.button !== 2 && !(event.button === 0 && groupBackground)) return;
  event.preventDefault();
  event.stopPropagation();
  cancelPan?.();
  const pointerId = event.pointerId;
  let x = event.clientX, y = event.clientY;
  panning.value = true;
  const move = (next: PointerEvent) => {
    if (next.pointerId !== pointerId) return;
    next.preventDefault();
    panBy({ x: next.clientX - x, y: next.clientY - y });
    x = next.clientX; y = next.clientY;
  };
  const end = (next: PointerEvent) => { if (next.pointerId === pointerId) cleanup(); };
  const cleanup = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", end);
    window.removeEventListener("pointercancel", end);
    window.removeEventListener("blur", cleanup);
    panning.value = false;
    cancelPan = undefined;
  };
  cancelPan = cleanup;
  window.addEventListener("pointermove", move, { passive: false });
  window.addEventListener("pointerup", end);
  window.addEventListener("pointercancel", end);
  window.addEventListener("blur", cleanup);
}
onBeforeUnmount(() => cancelPan?.());
watch(editorActive, active => { if (!active) cancelPan?.(); }, { flush: "sync" });
const mainId = ref<number | null>(null);
const page = ref(0);
const nodes = ref<Node[]>([]);
const edges = ref<Edge[]>([]);
const selectedEdge = ref<QuestFlowEdge>();
const positions = new Map<string, { x: number; y: number }>();
const parents = new Map<string, string | undefined>();
const projection = computed(() => buildQuestFlow(props.project, mainId.value, page.value));
const selectedSub = computed(() => props.selection?.kind === "sub" ? props.project.subQuests.find(sub => sub.id === props.selection?.id) : undefined);
let fittedScope = "";
let active = true;

function fit() { void nextTick(() => { if (active && editorActive()) void fitView({ padding: .22, duration: 250, maxZoom: 1 }); }); }
onActivated(() => { active = true; fit(); });
onDeactivated(() => { active = false; cancelPan?.(); });
function updateNodes(result: ReturnType<typeof buildQuestFlow>, reset = false) {
  const boxes = layoutQuestFlowGroups(result.nodes, result.edges, result.groups);
  if (reset) { positions.clear(); parents.clear(); }
  // Cache canvas coordinates so resizing a parent or changing pages never shifts its children.
  const absolute = questFlowAbsolutePositions(boxes);
  boxes.forEach((box, id) => {
    const saved = positions.get(id);
    if (saved && parents.get(id) === box.parentId) absolute.set(id, saved);
  });
  boxes.forEach((box, id) => {
    const position = absolute.get(id)!;
    const parent = box.parentId ? absolute.get(box.parentId)! : { x: 0, y: 0 };
    box.position = { x: position.x - parent.x, y: position.y - parent.y };
  });
  fitQuestFlowGroupBounds(boxes, result.groups);
  rememberPositions(boxes);
  function geometry(id: string) {
    const box = boxes.get(id)!;
    return { position: box.position, parentNode: box.parentId, width: box.width, height: box.height };
  }
  // Vue Flow needs parents before children to resolve nested positions.
  nodes.value = [
    ...result.groups.map(group => ({ id: group.id, type: "quest-group", ...geometry(group.id), data: group,
      draggable: true, dragHandle: ".group-heading", selectable: false, connectable: false, zIndex: group.kind === "chapter" ? 0 : 1 })),
    ...result.nodes.map(node => ({ id: node.id, type: "quest", ...geometry(node.id), data: node,
      selected: node.questId === selectedSub.value?.id, draggable: true, connectable: !node.missing, zIndex: 3 })),
  ];
}
watch(projection, result => {
  selectedEdge.value = undefined;
  updateNodes(result);
  edges.value = result.edges.map(edge => ({ id: edge.id, source: edge.source, target: edge.target,
    // Vue Flow adds one level for each parent: main groups render at 2, task cards at 4.
    // Keep the entire edge (path, arrow and label) above group fills and below task cards.
    sourceHandle: edge.kind, targetHandle: "in", type: "smoothstep", data: edge, zIndex: 3,
    label: edge.kind === "failure" ? "失败回溯" : `后续 ${edge.index + 1}`,
    markerEnd: { type: MarkerType.ArrowClosed, color: edge.kind === "failure" ? "#d89a51" : "#185abd" },
    style: { stroke: edge.kind === "failure" ? "#d89a51" : "#185abd", strokeWidth: edge.kind === "failure" ? 2 : 3, strokeDasharray: edge.kind === "failure" ? "6 4" : undefined },
    labelStyle: { fill: edge.kind === "failure" ? "#667997" : "#17417b", fontSize: 11, fontWeight: edge.kind === "failure" ? 400 : 600 }, labelBgStyle: { fill: "#fff", fillOpacity: 1 },
  }));
  const scope = `${mainId.value}:${result.page}`;
  if (scope !== fittedScope) { fittedScope = scope; fit(); }
}, { immediate: true });

watch(() => props.selection, selection => {
  if (selection?.kind === "main") { mainId.value = selection.id; page.value = 0; }
  if (selection?.kind === "sub") {
    const selected = props.project.subQuests.find(sub => sub.id === selection.id);
    if (selected) {
      if (mainId.value !== null && mainId.value !== selected.mainQuestId) mainId.value = selected.mainQuestId;
      const filtered = props.project.subQuests.filter(sub => mainId.value === null || sub.mainQuestId === mainId.value);
      page.value = Math.floor(filtered.findIndex(sub => sub.id === selected.id) / QUEST_FLOW_PAGE_SIZE);
    }
  }
  nodes.value = nodes.value.map(node => ({ ...node, selected: selection?.kind === "sub" && node.id === questNodeId(selection.id) }));
}, { immediate: true });
watch(() => props.project, () => { cancelPan?.(); positions.clear(); parents.clear(); mainId.value = null; page.value = 0; fittedScope = ""; }, { flush: "sync" });

function changeMain(event: Event) {
  const value = (event.target as HTMLSelectElement).value;
  mainId.value = value === "" ? null : Number(value);
  page.value = 0;
}
function arrange() {
  updateNodes(projection.value, true);
  fit();
}
function rememberPositions(boxes: Map<string, QuestFlowBox>) {
  questFlowAbsolutePositions(boxes).forEach((position, id) => positions.set(id, position));
  boxes.forEach((box, id) => parents.set(id, box.parentId));
}
function dragStop(event: NodeDragEvent) {
  const dragged = new Map(event.nodes.map(node => [node.id, node.position]));
  const boxes = new Map<string, QuestFlowBox>(nodes.value.map(node => [node.id, {
    position: { ...(dragged.get(node.id) ?? node.position) }, parentId: node.parentNode,
    width: Number(node.width) || 226, height: Number(node.height) || 132,
  }]));
  fitQuestFlowGroupBounds(boxes, projection.value.groups);
  // Update in place while dragging to preserve Vue Flow's active drag session.
  nodes.value.forEach(node => {
    const box = boxes.get(node.id)!;
    node.position = box.position; node.width = box.width; node.height = box.height;
  });
  rememberPositions(boxes);
}
function selectNode({ node }: NodeMouseEvent) {
  selectedEdge.value = undefined;
  if (node.type !== "quest") return;
  if (!node.data.missing && node.data.questId !== null) emit("select", node.data.questId);
}
function editNode({ node }: NodeMouseEvent) {
  if (node.type !== "quest") return;
  if (!node.data.missing && node.data.questId !== null) emit("edit", node.data.questId);
}
function selectEdge({ edge }: EdgeMouseEvent) { selectedEdge.value = projection.value.edges.find(item => item.id === edge.id); }
function connect(connection: Connection) {
  const source = projection.value.nodes.find(node => node.id === connection.source);
  const target = projection.value.nodes.find(node => node.id === connection.target);
  if (!source || !target || source.missing || target.missing || source.questId === null || target.questId === null) return;
  const message = connectQuestFlow(props.project, source.questId, target.questId, connection.sourceHandle === "failure" ? "failure" : "next");
  if (message) toast.warning(message);
}
function removeLink() {
  if (selectedEdge.value) disconnectQuestFlow(props.project, selectedEdge.value);
  selectedEdge.value = undefined;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[10] || (_cache[10] = _createElementVNode("strong", null, "任务衔接", -1)),
        _createVNode(StudioSelectField, {
          value: mainId.value ?? '',
          "aria-label": "衔接图主任务范围",
          onChange: changeMain
        }, {
          default: _withCtx(() => [
            _cache[9] || (_cache[9] = _createElementVNode("option", { value: "" }, "全部主任务", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.project.mainQuests, (main) => {
              return (_openBlock(), _createElementBlock("option", {
                key: main.id,
                value: main.id
              }, _toDisplayString(main.title || '未命名主任务') + " #" + _toDisplayString(main.id), 9, _hoisted_4))
            }), 128))
          ]),
          _: 1
        }, 8, ["value"])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("button", {
          type: "button",
          onClick: arrange
        }, "整理布局"),
        _createElementVNode("button", {
          type: "button",
          onClick: fit
        }, "适应画布"),
        _createElementVNode("button", {
          type: "button",
          "aria-label": "缩小任务图",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(zoomOut)()))
        }, "−"),
        _createElementVNode("button", {
          type: "button",
          "aria-label": "放大任务图",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(zoomIn)()))
        }, "＋")
      ])
    ]),
    _cache[14] || (_cache[14] = _createElementVNode("div", { class: "flow-guide" }, [
      _createElementVNode("span", { class: "legend-next" }, "后续任务"),
      _createElementVNode("span", { class: "legend-failure" }, "失败回溯"),
      _createElementVNode("span", null, "自由拖动子任务，外框自动包裹 · 拖动分组标题整体移动 · 双击子任务编辑属性")
    ], -1)),
    (projection.value.omitted)
      ? (_openBlock(), _createElementBlock("p", _hoisted_6, "当前图省略了 " + _toDisplayString(projection.value.omitted) + " 条连线，任务数据完整保留。可切换主任务或在任务属性中查看全部衔接。", 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["flow-canvas", { 'is-panning': panning.value }]),
      onPointerdownCapture: startCanvasPan,
      onContextmenu: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["prevent"]))
    }, [
      (nodes.value.length)
        ? (_openBlock(), _createBlock(_unref(VueFlow), {
            key: 0,
            id: flowId,
            nodes: nodes.value,
            "onUpdate:nodes": _cache[2] || (_cache[2] = ($event: any) => ((nodes).value = $event)),
            edges: edges.value,
            "onUpdate:edges": _cache[3] || (_cache[3] = ($event: any) => ((edges).value = $event)),
            "min-zoom": .15,
            "max-zoom": 1.6,
            "delete-key-code": null,
            "disable-keyboard-a11y": !_unref(editorActive)(),
            "edges-updatable": false,
            "pan-on-drag": [0],
            "fit-view-on-init": "",
            onConnect: connect,
            onNodeClick: selectNode,
            onNodeDoubleClick: editNode,
            onNodeDrag: dragStop,
            onNodeDragStop: dragStop,
            onEdgeClick: selectEdge,
            onPaneClick: _cache[4] || (_cache[4] = ($event: any) => (selectedEdge.value = undefined))
          }, {
            "node-quest-group": _withCtx((slot) => [
              _createElementVNode("div", {
                class: _normalizeClass(["flow-group", 'group-' + slot.data.kind])
              }, [
                _createElementVNode("header", {
                  class: "group-heading",
                  title: '拖动整体移动：' + slot.data.title
                }, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(slot.data.kind === 'chapter' ? (slot.data.entityId === null ? '直属' : '章节') : '主任务'), 1),
                  _createElementVNode("strong", null, _toDisplayString(slot.data.title), 1),
                  (slot.data.entityId !== null)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_9, "#" + _toDisplayString(slot.data.entityId), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(slot.data.count === slot.data.total ? slot.data.total : slot.data.count + '/' + slot.data.total) + " " + _toDisplayString(slot.data.kind === 'chapter' ? '个主任务' : '个子任务'), 1)
                ], 8, _hoisted_7),
                (!slot.data.count)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(slot.data.kind === 'chapter' ? '暂无主任务' : '暂无子任务'), 1))
                  : _createCommentVNode("", true)
              ], 2)
            ]),
            "node-quest": _withCtx((slot) => [
              _createElementVNode("div", {
                class: _normalizeClass(["flow-node", { 'is-selected': slot.selected, 'is-boundary': slot.data.boundary, 'is-missing': slot.data.missing }])
              }, [
                _createVNode(_unref(Handle), {
                  id: "in",
                  type: "target",
                  position: _unref(Position).Left,
                  connectable: !slot.data.missing
                }, null, 8, ["position", "connectable"]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("span", null, _toDisplayString(slot.data.questId === null ? '空引用' : '#' + slot.data.questId), 1),
                  (slot.data.boundary && !slot.data.missing)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_13, "范围外关联"))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("strong", {
                  title: slot.data.title
                }, _toDisplayString(slot.data.title), 9, _hoisted_14),
                _createElementVNode("small", {
                  title: slot.data.parent
                }, _toDisplayString(slot.data.parent), 9, _hoisted_15),
                (!slot.data.missing && !slot.data.boundary)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                      (slot.data.finish)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_17, "完成主任务"))
                        : _createCommentVNode("", true),
                      _cache[11] || (_cache[11] = _createElementVNode("span", { class: "port-label next-port" }, "后续", -1)),
                      _cache[12] || (_cache[12] = _createElementVNode("span", { class: "port-label failure-port" }, "回溯", -1))
                    ]))
                  : _createCommentVNode("", true),
                (!slot.data.missing && !slot.data.boundary)
                  ? (_openBlock(), _createBlock(_unref(Handle), {
                      key: 1,
                      id: "next",
                      type: "source",
                      position: _unref(Position).Right,
                      style: { top: '78px' }
                    }, null, 8, ["position"]))
                  : _createCommentVNode("", true),
                (!slot.data.missing && !slot.data.boundary)
                  ? (_openBlock(), _createBlock(_unref(Handle), {
                      key: 2,
                      id: "failure",
                      type: "source",
                      position: _unref(Position).Right,
                      style: { top: '108px', background: '#d89a51' }
                    }, null, 8, ["position"]))
                  : _createCommentVNode("", true)
              ], 2)
            ]),
            default: _withCtx(() => [
              _createVNode(_unref(Background), {
                "pattern-color": "#aab9d5",
                gap: 22,
                size: 1
              })
            ]),
            _: 1
          }, 8, ["nodes", "edges", "disable-keyboard-a11y"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_18, [...(_cache[13] || (_cache[13] = [
            _createElementVNode("strong", null, "从子任务开始建立衔接", -1),
            _createElementVNode("p", null, "在左侧选择主任务，再点击上方「＋ 子任务」。", -1)
          ]))]))
    ], 34),
    _createElementVNode("footer", _hoisted_19, [
      (selectedEdge.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
            _createElementVNode("span", null, "#" + _toDisplayString(selectedEdge.value.sourceId) + " → " + _toDisplayString(selectedEdge.value.targetId === null ? '空引用' : '#' + selectedEdge.value.targetId) + " · " + _toDisplayString(selectedEdge.value.kind === 'failure' ? '失败回溯' : '后续第 ' + (selectedEdge.value.index + 1) + ' 项'), 1),
            _createElementVNode("button", {
              type: "button",
              class: "danger",
              onClick: removeLink
            }, "移除此连线")
          ]))
        : (selectedSub.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
              _createElementVNode("span", null, _toDisplayString(selectedSub.value.title || '未命名子任务') + " · #" + _toDisplayString(selectedSub.value.id), 1),
              _createElementVNode("button", {
                type: "button",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (emit('edit', selectedSub.value.id)))
              }, "编辑任务属性")
            ]))
          : (_openBlock(), _createElementBlock("span", _hoisted_22, "点击连线可移除关联；拖动卡片可调整布局。")),
      (projection.value.pageCount > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
            _createElementVNode("button", {
              type: "button",
              disabled: projection.value.page === 0,
              onClick: _cache[7] || (_cache[7] = ($event: any) => (page.value = projection.value.page - 1))
            }, "上一页", 8, _hoisted_24),
            _createElementVNode("span", null, _toDisplayString(projection.value.page + 1) + "/" + _toDisplayString(projection.value.pageCount) + " · " + _toDisplayString(projection.value.total) + " 个子任务", 1),
            _createElementVNode("button", {
              type: "button",
              disabled: projection.value.page + 1 >= projection.value.pageCount,
              onClick: _cache[8] || (_cache[8] = ($event: any) => (page.value = projection.value.page + 1))
            }, "下一页", 8, _hoisted_25)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})